"use client";

// Une question de FAQ, dépliable.
//
// Le déclencheur était un div avec onClick : impossible à ouvrir au clavier, et
// muet pour un lecteur d'écran. C'est maintenant un vrai bouton, dans un titre
// de niveau 3, qui annonce son état et le panneau qu'il commande.
//
// La réponse reste dans le DOM en permanence (repliée par une hauteur animée,
// avec l'attribut hidden quand elle est fermée) au lieu d'être démontée. Deux
// raisons : un lecteur d'écran peut annoncer la relation bouton/panneau, et
// surtout le texte des réponses devient indexable, ce qu'il n'était pas.

// Libraries
import { useId, useState } from "react";
import { motion } from "motion/react";

export default function Question(props) {
    // State
    const [showAnswer, setShowAnswer] = useState(false);
    const panelId = `faq-panel-${useId()}`;

    return (
        <motion.div
            className="group"
            variants={{
                hidden: { y: 20, opacity: 0 },
                visible: {
                    y: 0,
                    opacity: 1,
                },
            }}
        >
            <div
                className={`relative rounded-2xl border transition-all duration-300 overflow-hidden ${
                    showAnswer
                        ? "bg-white dark:bg-newDark-700 border-gray-200 dark:border-white/10 shadow-lg shadow-gray-200/50 dark:shadow-black/20"
                        : "bg-white dark:bg-newDark-700/50 border-gray-200/60 dark:border-white/[0.06] hover:border-gray-300 dark:hover:border-white/10 hover:shadow-md hover:shadow-gray-200/30 dark:hover:shadow-none"
                }`}
            >
                {/* Left accent bar */}
                <div
                    aria-hidden="true"
                    className={`absolute left-0 top-0 w-[3px] rounded-l-2xl transition-all duration-500 ${
                        showAnswer
                            ? "h-full bg-primary-100"
                            : "h-0 bg-primary-100/0 group-hover:h-1/3 group-hover:bg-primary-100/40"
                    }`}
                />

                {/* Question header */}
                <h3 className="mb-0">
                    <button
                        type="button"
                        onClick={() => setShowAnswer(!showAnswer)}
                        aria-expanded={showAnswer}
                        aria-controls={panelId}
                        className="flex w-full cursor-pointer items-center gap-4 px-6 py-5 text-left"
                    >
                        {/* Animated icon */}
                        <span
                            aria-hidden="true"
                            className={`relative flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-300 ${
                                showAnswer
                                    ? "bg-primary-100/10 dark:bg-primary-100/15"
                                    : "bg-gray-100 dark:bg-white/5 group-hover:bg-primary-100/5 dark:group-hover:bg-primary-100/10"
                            }`}
                        >
                            <motion.span
                                className="block"
                                animate={{ rotate: showAnswer ? 45 : 0 }}
                                transition={{
                                    duration: 0.3,
                                    ease: [0.4, 0, 0.2, 1],
                                }}
                            >
                                <svg
                                    className={`w-4 h-4 transition-colors duration-300 ${
                                        showAnswer
                                            ? "text-primary-100"
                                            : "text-gray-400 dark:text-grayDark-500 group-hover:text-primary-100"
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M12 6v12M6 12h12"
                                    />
                                </svg>
                            </motion.span>
                        </span>

                        {/* Question text */}
                        <span
                            className={`text-[15px] font-semibold leading-snug transition-colors duration-300 ${
                                showAnswer
                                    ? "text-gray-900 dark:text-white"
                                    : "text-gray-700 dark:text-grayDark-200 group-hover:text-gray-900 dark:group-hover:text-white"
                            }`}
                        >
                            {props.summary}
                        </span>
                    </button>
                </h3>

                {/* Answer content — animated, mais jamais démontée.
                    `inert` plutôt que `hidden` : hidden pose display:none, ce
                    qui tuerait l'animation de repli. inert retire le panneau du
                    focus et des lecteurs d'écran quand il est fermé, tout en
                    laissant le texte dans le HTML servi, donc indexable. */}
                <motion.div
                    id={panelId}
                    inert={!showAnswer}
                    initial={false}
                    animate={{
                        height: showAnswer ? "auto" : 0,
                        opacity: showAnswer ? 1 : 0,
                    }}
                    transition={{
                        height: { duration: 0.35, ease: [0.4, 0, 0.2, 1] },
                        opacity: { duration: 0.25, delay: showAnswer ? 0.1 : 0 },
                    }}
                    className="overflow-hidden"
                >
                    <div className="px-6 pb-6 pl-[4.5rem]">
                        <div className="text-[14px] leading-relaxed text-gray-500 dark:text-grayDark-400 [&>p]:mb-2 [&>p:last-child]:mb-0">
                            {props.children}
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
}
